<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { IFormPlainDataListboxPropsChangeState, IFormSelectInputOption } from '@modules/forms/types';
import type { IPostbackCondition, IPostbackUriOption, IPostbackWebhook, IPostbackWebhookSaveResponse } from '@modules/postbacks/submodules/edit/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EAasmState } from '@modules/core/enums';
import { isServerValidationError } from '@modules/core/errors';
import VFormCheckbox from '@modules/forms/components/inputs/VFormCheckbox/VFormCheckbox.vue';
import VFormPlainDataListbox from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';
import VFormSelectInput from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePostbackTypes } from '@modules/postbacks/composables';
import {
  ADMIN_POSTBACKS_LIST_ROUTE_NAME,
  AFFILIATE_POSTBACKS_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_POSTBACKS,
} from '@modules/postbacks/constants';
import { usePostbacksAffiliateGetQuery } from '@modules/postbacks/queries';
import PostbackConditions
  from '@modules/postbacks/submodules/edit/components/PostbackConditions/PostbackConditions.vue';
import PostbackUrlBuilder
  from '@modules/postbacks/submodules/edit/components/PostbackUrlBuilder/PostbackUrlBuilder.vue';
import {
  usePostbackConditionRemoveMutation,
  usePostbackConditionSaveMutation,
  usePostbackTrackersSaveMutation,
  usePostbackUrlOptionDeleteMutation,
  usePostbackUrlOptionSaveMutation,
  useWebhookSaveMutation,
} from '@modules/postbacks/submodules/edit/mutations';
import { usePostbacksGetQuery } from '@modules/postbacks/submodules/edit/queries';
import {
  EWebhookUriOptionFields,
} from '@modules/postbacks/submodules/edit/types';
import { useUserSupervision } from '@modules/user/composables';
import { isString } from '@utils/guards';

const { getTranslation } = useI18n();
const { t } = useTranslation();
const route = useRoute();
const { isAdminNamespace } = useCurrentNamespace();
const { confirmDelete } = useConfirmDialogs();
const { isSupervision } = useUserSupervision();

const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : undefined));
const id = computed(() => (isString(route.params.postbackId) ? Number(route.params.postbackId) : 0));
const type = computed(() => (isString(route.params.type) ? route.params.type : undefined));

const { data: affiliate } = usePostbacksAffiliateGetQuery(affiliateId);
const {
  data: postbackData,
  isLoading,
  setData,
} = usePostbacksGetQuery(id, affiliateId);

const { typeOptions } = usePostbackTypes();

const title = computed(() => {
  if (isAdminNamespace.value) {
    const baseHeader = getTranslation(TRANSLATION_GROUP_POSTBACKS, 'ADMIN_SETTINGS_HEADER').value;

    if (affiliate.value) {
      return `${baseHeader} ${affiliateId.value} - ${affiliate.value.email} - ${affiliate.value.username}`;
    }
  }

  return getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_HEADER').value;
});

const {
  mutateAsync: saveWebhook,
  data: savedWebhook,
  errors: saveErrors,
  clearError: clearSaveError,
} = useWebhookSaveMutation();

const saveErrorsTranslated = computed<Record<string, string[]> | undefined>(() => {
  if (!saveErrors.value) {
    return undefined;
  }
  const result: Record<string, string[]> = {};

  for (const [key, value] of Object.entries(saveErrors.value)) {
    result[key] = value.map((item) => t(item.locale_key, item.values));
  }

  return result;
});

async function editPostback(postback: IPostbackWebhook, saveRequest = true): Promise<IPostbackWebhookSaveResponse | undefined> {
  setData((data) => {
    if (!data) {
      return undefined;
    }

    return {
      ...data,
      webhook: {
        ...data.webhook,
        ...postback,
      },
    };
  });

  if (saveRequest) {
    clearSaveError('uri');
    return saveWebhook({
      ...postback,
      affiliateId: affiliateId.value,
    });
  }

  return undefined;
}

watch(savedWebhook, (newValue) => {
  if (newValue?.webhook) {
    void editPostback(newValue.webhook, false);
  }
});

function onChangeSuccessConfirmation(value?: boolean): void {
  if (!postbackData.value) {
    return;
  }

  void editPostback({
    ...postbackData.value.webhook,
    success_confirmation: !!value,
  });
}

function onChangeActive(value?: boolean): void {
  if (!postbackData.value) {
    return;
  }

  void editPostback({
    ...postbackData.value.webhook,
    active: !!value,
  });
}

const backUrl = computed<RouteLocationRaw>(() => {
  if (isAdminNamespace.value) {
    return {
      name: ADMIN_POSTBACKS_LIST_ROUTE_NAME,
      params: {
        type: type.value,
        id: affiliateId.value,
      },
    };
  }

  return {
    name: AFFILIATE_POSTBACKS_LIST_ROUTE_NAME,
    params: {
      type: type.value,
    },
  };
});

const {
  mutateAsync: saveTracker,
  isLoading: isTrackerSaving,
} = usePostbackTrackersSaveMutation();

function onChangeTracker(state: IFormPlainDataListboxPropsChangeState<number>): void {
  void saveTracker({
    value: state.value,
    search: state.search,
    id: id.value,
    affiliateId: affiliateId.value,
    addType: state.check ? 'add' : 'remove',
  });
}

const obtainedResultOptions = computed<IFormSelectInputOption<string>[]>(() => {
  if (!postbackData.value?.condition_options?.obtained_result) {
    return [];
  }

  return Object.entries(postbackData.value.condition_options.obtained_result).map(([value, label]) => ({
    value,
    label,
  }));
});

const {
  mutateAsync: saveCondition,
} = usePostbackConditionSaveMutation();
const {
  mutateAsync: removeCondition,
} = usePostbackConditionRemoveMutation();

async function onConditionChange(values: IPostbackCondition): Promise<void> {
  const { condition } = await saveCondition({
    ...values,
    postbackId: id.value,
    affiliateId: affiliateId.value,
  });

  setData((data) => {
    if (!data) {
      return undefined;
    }

    const conditions = [...(data.conditions ?? [])];
    if (condition.id) {
      const index = conditions.findIndex((item) => (
        item.id === condition.id
      ));

      if (index > -1) {
        conditions.splice(index, 1, condition);
      } else {
        conditions.push(condition);
      }
    }

    return {
      ...data,
      conditions,
    };
  });
}

async function deleteCondition(values: IPostbackCondition): Promise<void> {
  setData((data) => {
    if (!data) {
      return undefined;
    }

    const conditions = [...(data.conditions ?? [])];
    if (values.id) {
      const index = conditions.findIndex((item) => (
        item.id === values.id
      ));

      if (index > -1) {
        conditions.splice(index, 1);
      }
    }

    return {
      ...data,
      conditions,
    };
  });

  await removeCondition({
    ...values,
    postbackId: id.value,
    affiliateId: affiliateId.value,
  });
}

function onConditionRemove(values: IPostbackCondition): void {
  confirmDelete({
    header: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_REMOVE_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_REMOVE_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_REMOVE_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_REMOVE_NO').value,
    accept: () => {
      void deleteCondition(values);
    },
  });
}

const uriOptionErrors = ref<Record<number, string[]>>({});
const newOptionErrors = ref<string[]>([]);

const uriOptions = computed<IPostbackUriOption[]>(() => (
  [
    ...(postbackData.value?.uri_options ?? []),
    {
      aasm_state: EAasmState.CUSTOM,
      active: true,
      description: '',
      key: '',
      value: '',
    } as IPostbackUriOption,
  ].map((item) => ({
    ...item,
    errors: (item.id ? uriOptionErrors.value[item.id] : newOptionErrors.value),
  }))
));

const {
  mutateAsync: saveUriOption,
  isLoading: isSavingUriOption,
} = usePostbackUrlOptionSaveMutation();
const {
  mutateAsync: removeUriOption,
} = usePostbackUrlOptionDeleteMutation();

function handleUrlOptionChangeError(error: unknown, values: IPostbackUriOption): void {
  if (isServerValidationError<IApiFormErrors>(error)) {
    const errors: string[] = [];

    for (const errorsError of Object.values(error.errors)) {
      if (Array.isArray(errorsError)) {
        errors.push(...errorsError.map((val) => t(val.locale_key, val.values)));
      }
    }

    if (!values.id) {
      newOptionErrors.value = errors;
    } else {
      uriOptionErrors.value = {
        ...uriOptionErrors.value,
        [values.id]: errors,
      };
    }

    return;
  }

  throw error;
}

async function onUrlOptionChange(values: IPostbackUriOption): Promise<void> {
  try {
    if (!values.id) {
      newOptionErrors.value = [];
    } else if (uriOptionErrors.value[values.id]) {
      uriOptionErrors.value[values.id] = [];
    }

    const { uri_option } = await saveUriOption({
      ...values,
      webhook_id: id.value,
      postbackId: id.value,
      affiliateId: affiliateId.value,
    });

    setData((data) => {
      if (!data) {
        return undefined;
      }

      const uri_options = [...data.uri_options];
      if (uri_option.id) {
        const index = uri_options.findIndex((item) => (
          item.id === uri_option.id
        ));

        if (index > -1) {
          uri_options.splice(index, 1, uri_option);
        } else {
          uri_options.push(uri_option);
        }
      }

      return {
        ...data,
        uri_options,
      };
    });
  } catch (error) {
    handleUrlOptionChangeError(error, values);
  }
}

async function extractParamsFromUrl(urlObj: URL): Promise<void> {
  const uri = `${urlObj.origin}${urlObj.pathname}`;

  if (postbackData.value?.webhook) {
    await editPostback({ ...postbackData.value.webhook, uri });
    for (const [key, value] of urlObj.searchParams.entries()) {
      const foundOption = uriOptions.value.find((a) => key === a[EWebhookUriOptionFields.KEY]);
      if (foundOption?.aasm_state === EAasmState.DEFAULT)
        continue;
      const optionToSubmit: IPostbackUriOption = foundOption
        ? { ...foundOption, value }
        : {
            aasm_state: EAasmState.CUSTOM,
            active: true,
            key,
            value,
            description: '',
          };

      await onUrlOptionChange(optionToSubmit);
    }
  }
}

async function onDeleteUriOption(values: IPostbackUriOption): Promise<void> {
  setData((data) => {
    if (!data) {
      return undefined;
    }

    const uri_options = [...data.uri_options];
    if (values.id) {
      const index = uri_options.findIndex((item) => (
        item.id === values.id
      ));

      if (index > -1) {
        uri_options.splice(index, 1);
      }
    }

    return {
      ...data,
      uri_options,
    };
  });

  await removeUriOption({
    ...values,
    webhook_id: id.value,
    postbackId: id.value,
    affiliateId: affiliateId.value,
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'PostbacksEditPage'"
    type="form"
    :title="title"
  >
    <VLoader
      :loading="isLoading"
      min-height="400px"
    >
      <div>
        <VFormSelectInput
          :options="typeOptions"
          :value="postbackData?.webhook.kind"
          :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_TYPE')"
          readonly
          class="mb-8"
        />
        <div
          class="mb-3 text-lg font-medium"
        >
          {{ $getTranslation(TRANSLATION_GROUP_POSTBACKS, 'URL_LABEL') }}
        </div>
        <div class="mb-4">
          <PostbackUrlBuilder
            :postback-data="postbackData"
            :uri-options="uriOptions"
            :errors="saveErrorsTranslated"
            :is-saving-uri-option="isSavingUriOption"
            @change-webhook="editPostback"
            @change-uri-option="onUrlOptionChange"
            @remove-uri-option="onDeleteUriOption"
            @url-to-extract-change="extractParamsFromUrl"
          />
        </div>
        <div class="mb-4">
          <VFormCheckbox
            :value="postbackData?.webhook.success_confirmation"
            :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_SUCCESS_CONFIRMATION')"
            :readonly="postbackData?.webhook.build"
            :disabled="isSupervision"
            @change="onChangeSuccessConfirmation"
          />
        </div>
        <div class="mb-4">
          <VFormCheckbox
            :value="postbackData?.webhook.active"
            :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_ACTIVE')"
            :readonly="postbackData?.webhook.build"
            :disabled="isSupervision"
            @change="onChangeActive"
          />
        </div>
        <div
          v-if="postbackData?.condition_options?.condition_for_comparison"
          class="mb-4"
        >
          <PostbackConditions
            :comparison-options="postbackData?.condition_options?.condition_for_comparison || {}"
            :items="postbackData?.conditions || []"
            :obtained-result-options="obtainedResultOptions"
            :readonly="postbackData?.webhook.build || isSupervision"
            @change="onConditionChange"
            @add="onConditionChange"
            @remove="onConditionRemove"
          />
        </div>
        <div class="mb-8">
          <VFormPlainDataListbox
            :data="postbackData?.serial_ids || []"
            :readonly="postbackData?.webhook.build"
            :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_CHECKBOXES')"
            :disabled="isTrackerSaving || isSupervision"
            sort-after-change-item
            @change="onChangeTracker"
          />
        </div>
        <div>
          <VBackButton
            :to="backUrl"
            :label="$getTranslation(TRANSLATION_GROUP_POSTBACKS, 'SETTINGS_BACK')"
          />
        </div>
      </div>
    </VLoader>
  </VCard>
</template>
